import { FilloutStandardEmbed } from "@fillout/react";
import { Box, Grid, useMediaQuery, useTheme } from "@mui/material";
import {
  addFilledOut,
  ProgrammeActions,
  selectAllFarmerProgrammeDashboards,
  selectProgrammeActionsById,
  selectSubmittedActionsEntriesByProgrammeId,
  useAppDispatch,
  useAppSelector,
} from "model";
import { setQuestionnaireFinished } from "model/questionnaireStartedSlice";
import { useEffect } from "react";
import { useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import NavigationLock from "./NavigationLock";

export default function ProgrammesAction() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { programmeId } = useParams<{ programmeId: string }>();
  const history = useHistory();

  const allFarmerDashboards = useAppSelector(
    selectAllFarmerProgrammeDashboards
  );

  const farmerDashboards = allFarmerDashboards.filter(
    (dashboard) => dashboard.programmeId === programmeId
  );

  const farmerDashboard =
    farmerDashboards.length > 0 ? farmerDashboards[0] : null;

  const questionnaireStarted = useSelector(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-return
    (state) => (state as any).questionnaireStarted
  );

  const dispatch = useAppDispatch();

  const programmeActions = useAppSelector((state) =>
    selectProgrammeActionsById(state, programmeId)
  );

  const actionPlanEntries = useAppSelector((state) =>
    selectSubmittedActionsEntriesByProgrammeId(state, programmeId)
  );
  const hasSelectedActions = actionPlanEntries?.length === 0;
  const onSubmit = () => {
    dispatch(addFilledOut({ filledOutProgrammeId: programmeId }));
    dispatch(setQuestionnaireFinished(true));
  };

  // Listen to route changes and cleanup questionnaireStarted state when navigating away
  useEffect(() => {
    const unsetQuestionnaireStarted = history.listen((e) => {
      if (e.pathname !== `/programme-details/${programmeId}`) {
        // User is navigating away from programme details page
        dispatch(setQuestionnaireFinished(true));
      }
    });

    return function cleanup() {
      unsetQuestionnaireStarted();
    };
  }, [dispatch, history, programmeId]);

  // Prevent accidental page close/refresh when questionnaire is in progress
  useEffect(() => {
    const onBeforeUnload = (ev: Event) => {
      if (questionnaireStarted.questionnaireStarted) {
        ev.preventDefault();
      }
    };
    window.addEventListener("beforeunload", onBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionnaireStarted.questionnaireStarted]);

  if (
    questionnaireStarted.questionnaireStarted &&
    programmeActions?.filloutId
  ) {
    return (
      <>
        <NavigationLock when={questionnaireStarted.questionnaireStarted} />
        {programmesActionsIframe(programmeActions, onSubmit)}
      </>
    );
  } else {
    // there simply might be no dashboard
    return <></>;
  }
}

const programmesActionsIframe = (
  programmesActions: ProgrammeActions,
  onSubmit: () => void
) => {
  return (
    <Grid
      item
      xs={12}
      sx={{ flex: 1, display: "flex", flexDirection: "column" }}
    >
      <Box
        sx={{
          backgroundColor: "white",
          borderRadius: 0,
          boxShadow: 0,
          padding: 0,
          margin: 0,
          overflow: "hidden",
          display: "flex",
          flexDirection: "row",
          height: "100vh",
          width: "100%",
          "& iframe": {
            width: "100% !important",
            maxWidth: "100% !important",
          },
          "& > div": {
            width: "100% !important",
            "& iframe": {
              width: "100% !important",
              maxWidth: "100% !important",
            },
          },
        }}
      >
        <FilloutStandardEmbed
          filloutId={programmesActions.filloutId || ""}
          onSubmit={onSubmit}
          parameters={programmesActions.filloutParameters}
        />
      </Box>
    </Grid>
  );
};
